import React from 'react';

import { Box, Button, Checkbox, FormikField, H1 } from '@betterment/samba-react';
import { Form, Formik } from 'formik';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';

import { Header } from './Header';

import AdvisoryAgreements from '../../Agreements/AdvisoryAgreements';
import TermsAndConditions from '../../Agreements/TermsAndConditions';

interface AccountAgreementsProps {
  logoUrl?: string;
  firmName: string;
  clientAgreementUrl?: string | null;
  clientAgreementToken?: string | null;
}

const getFormSchema = (hasClientAgreement: boolean) => {
  const baseSchema = {
    termsAndConditions: Yup.boolean().oneOf([true], 'You must accept this agreement to continue'),
  };

  if (hasClientAgreement) {
    return Yup.object().shape({
      ...baseSchema,
      advisoryAgreement: Yup.boolean().oneOf([true], 'You must accept this agreement to continue'),
    });
  }

  return Yup.object().shape(baseSchema);
};

export const AccountAgreements: React.FC<AccountAgreementsProps> = ({
  logoUrl,
  firmName,
  clientAgreementUrl,
  clientAgreementToken,
}) => {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const handleSubmit = async () => {
    // TODO: Add GraphQL mutation when backend is ready
    // For now, just navigate to success
    await navigate(`/accept_solo_401k/success?invitation_token=${params.get('invitation_token')}`);
  };

  return (
    <Box css={{ display: 'flex', flexDirection: 'column' }}>
      <Header logoUrl={logoUrl} progress={99} title="Account agreements" />

      <Box
        css={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignSelf: 'center',
          padding: 'space.400',
          paddingTop: 'space.800',
          width: '672px',
        }}
      >
        <H1
          variant="pageTitle"
          css={{
            marginBottom: 'space.150',
            color: 'text.DEFAULT',
            paddingBottom: 'space.200',
          }}
        >
          Review these agreements to open your account
        </H1>

        <Formik
          initialValues={{
            termsAndConditions: false,
            ...(clientAgreementUrl && { advisoryAgreement: false }),
          }}
          onSubmit={handleSubmit}
          validationSchema={getFormSchema(!!clientAgreementUrl)}
        >
          {({ isSubmitting }) => (
            <Form>
              <Box css={{ marginBottom: 'space.200' }}>
                <FormikField name="termsAndConditions">
                  <FormikField.Control>
                    <Checkbox>
                      <Checkbox.Control />
                      <Checkbox.Label>
                        By checking this box, you acknowledge that you have read and agree to the terms below, and
                        acknowledge the receipt of the documents contained therein.
                      </Checkbox.Label>
                    </Checkbox>
                  </FormikField.Control>
                  <FormikField.ErrorMessage />
                </FormikField>
              </Box>

              <TermsAndConditions includeSolo401kCopy={true} />

              {clientAgreementUrl && (
                <>
                  <Box css={{ marginTop: 'space.200' }}>
                    <FormikField name="advisoryAgreement">
                      <FormikField.Control>
                        <Checkbox>
                          <Checkbox.Control />
                          <Checkbox.Label>
                            By checking this box, you acknowledge and agree that you have read and agreed to the terms
                            below.
                          </Checkbox.Label>
                        </Checkbox>
                      </FormikField.Control>
                      <FormikField.ErrorMessage />
                    </FormikField>
                  </Box>

                  <AdvisoryAgreements
                    firmName={firmName}
                    agreements={{
                      clientAgreement: {
                        url: clientAgreementUrl,
                        token: clientAgreementToken!,
                      },
                    }}
                  />
                </>
              )}

              <Button type="submit" variant="solidPrimary" disabled={isSubmitting} css={{ marginTop: 'space.200' }}>
                Confirm Account
              </Button>
            </Form>
          )}
        </Formik>
      </Box>
    </Box>
  );
};
